import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import {
  type UppyFileWithMeta,
  type UploadResponse,
  ApiClientError,
  type FileUploadProgress,
  type UploadProgressData,
  type UploadResult,
} from "@/services/api";
import { toast } from "@/shared/lib/toast";
import { getAccessToken } from "@/shared/lib/auth-token";

export function extractFileIdFromUrl(uploadUrl: string): string | null {
  const match = uploadUrl.match(/\/files\/([^/?]+)/);
  return match?.[1] ?? null;
}

export function extractFileId(
  file: UppyFileWithMeta,
  response?: UploadResponse
): string | null {
  if (file.meta?.fileId) {
    return file.meta.fileId;
  }

  const uploadUrl =
    response?.uploadURL ||
    file.tus?.uploadUrl ||
    file.meta?.uploadUrl ||
    file.uploadURL;

  if (uploadUrl) {
    return extractFileIdFromUrl(uploadUrl);
  }

  return null;
}

export function extractErrorMessage(
  error: unknown,
  defaultMessage: string
): string {
  if (error instanceof ApiClientError) {
    return error.body?.detail || error.body?.message || error.message || defaultMessage;
  }
  if (error instanceof Error) {
    const errorMessage = error.message || defaultMessage;

    const responseTextMatch = errorMessage.match(/response text:\s*([^,]+)/i);
    if (responseTextMatch && responseTextMatch[1]) {
      return responseTextMatch[1].trim();
    }

    return errorMessage;
  }
  if (typeof error === "string") {
    const responseTextMatch = error.match(/response text:\s*([^,]+)/i);
    if (responseTextMatch && responseTextMatch[1]) {
      return responseTextMatch[1].trim();
    }
    return error;
  }
  return defaultMessage;
}

export function createAttributeValuesArray(
  attributeValues: Record<string | number, string | number | undefined>
) {
  return Object.entries(attributeValues)
    .filter(([, value]) => value !== undefined)
    .map(([attributeId, value]) => ({
      attributeId: Number(attributeId),
      value: String(value),
    }));
}

export function updateProgressStatus(
  prev: Map<string, FileUploadProgress>,
  fileId: string,
  updates: Partial<FileUploadProgress>
): Map<string, FileUploadProgress> {
  const next = new Map(prev);
  const current = next.get(fileId);
  if (current) {
    next.set(fileId, { ...current, ...updates });
  }
  return next;
}

export function calculateProgressPercent(
  progressData: UploadProgressData
): number {
  if (!progressData.bytesUploaded || !progressData.bytesTotal) return 0;
  return Math.round(
    (progressData.bytesUploaded / progressData.bytesTotal) * 100
  );
}

export function formatUploadMessage(
  successfulCount: number,
  failedCount: number
): string {
  if (failedCount === 0) {
    return `Successfully uploaded ${successfulCount} file${successfulCount > 1 ? "s" : ""}.`;
  }
  return `Successfully uploaded ${successfulCount} file${successfulCount > 1 ? "s" : ""}. ${failedCount} file${failedCount > 1 ? "s" : ""} failed.`;
}

export function createUppyInstance(
  endpoint: string,
  allowedFileTypes?: string[]
): Uppy {
  const instance = new Uppy({
    restrictions: {
      maxNumberOfFiles: 50,
      allowedFileTypes: allowedFileTypes ?? undefined,
    },
    autoProceed: false,
  });

  instance.use(Tus, {
    endpoint,
    retryDelays: [0, 1000, 3000, 5000],
    chunkSize: 5 * 1024 * 1024,
    limit: 1,
    onBeforeRequest: async (request) => {
      const token = await getAccessToken();
      if (token) {
        request.setHeader("Authorization", `Bearer ${token}`);
      }
    },
  });

  return instance;
}

export function overrideUppyError(
  uppy: Uppy,
  fileId: string,
  errorMessage: string
) {
  const override = () => {
    const file = uppy.getFile(fileId);
    if (file) (file as { error?: string | Error }).error = errorMessage;
  };
  override();
  [0, 50, 100, 200].forEach((delay) => setTimeout(override, delay));
}

export function showUploadToast(
  result: UploadResult,
  progressRef: React.MutableRefObject<Map<string, FileUploadProgress>>
) {
  const success = result.successful?.length ?? 0;
  const failed = result.failed?.length ?? 0;
  const failedFiles = result.failed || [];
  if (success > 0 && failed === 0) {
    toast.success(
      "Upload completed",
      `Successfully uploaded ${success} file${success > 1 ? "s" : ""}.`
    );
    return;
  }
  if (failed === 0) return;
  const errMsg =
    failedFiles
      .map((f) => progressRef.current.get(f.id)?.error)
      .filter((e): e is string => Boolean(e))[0] ||
    "All files failed to upload.";
  if (success === 0) {
    toast.error(
      "Upload failed",
      failedFiles.length === 1
        ? errMsg
        : `${failedFiles.length} files failed to upload. ${errMsg}`
    );
  } else {
    toast.error(
      "Partial upload",
      `${success} file${success > 1 ? "s" : ""} uploaded, ${failed} file${failed > 1 ? "s" : ""} failed. ${errMsg}`
    );
  }
}
